/**
 * The admin activity view on the wire (firm-model spec §9, R18; stage A6). Two kinds, the
 * panel's two tables: **runs** (a skill run finished: skill, version, scope, findings,
 * coverage, member, date) and **actions** (an issue fixed or dismissed: how it was fixed,
 * issue id, member, date, and the finding's text when the firm shares it). Read from
 * `skill_events` only; never chat threads, never slide content. Filters are server-side
 * (`GET /v1/firm/activity?kind&from&to&member&skill&page`). A new field is one edit here
 * (type + schema), then `npm run contract:sync`.
 */
import { z } from "zod";
import { expectParity } from "../parity.js";

export type ActivityKind = "runs" | "actions";

/** A strict ISO 8601 timestamp with an offset or Z (what the panel sends). Anything else is
 * a 400, never a value Postgres has to interpret. */
const isoDate = z.string().datetime({ offset: true });

/** The query string. `from` is inclusive, `to` exclusive; both ISO timestamps. */
export type ActivityQuery = {
  kind: ActivityKind;
  from?: string;
  to?: string;
  /** A member's user id. */
  member?: string;
  /** A skill id. */
  skill?: string;
  /** 1-based. */
  page: number;
};

export const activityQuerySchema = z.object({
  kind: z.enum(["runs", "actions"]).default("runs"),
  from: isoDate.optional(),
  to: isoDate.optional(),
  member: z.string().uuid().optional(),
  skill: z.string().min(1).max(64).optional(),
  page: z.coerce.number().int().min(1).max(10000).default(1),
});

/** A person the rows name (the member filter's options). Email null = no longer known. */
export type ActivityMember = { userId: string; email: string | null; name: string | null };

const activityMemberSchema = z.object({ userId: z.string(), email: z.string().nullable(), name: z.string().nullable() });

/** One finished skill run. Counts come from the run's coverage record, never its rows. */
export type ActivityRun = {
  id: string;
  at: string;
  userId: string;
  skillId: string | null;
  skillVersion: string | null;
  scope: "slide" | "deck" | null;
  /** Findings the run reported (checks + model); null when the run doesn't report any (flow skills). */
  findings: number | null;
  /** Slides read of the slides in scope; null when the run doesn't say. */
  coverage: { slidesRead: number; slidesTotal: number } | null;
  /** Whether the run completed; null when it doesn't say. */
  ok: boolean | null;
};

const activityRunSchema = z.object({
  id: z.string(),
  at: z.string(),
  userId: z.string(),
  skillId: z.string().nullable(),
  skillVersion: z.string().nullable(),
  scope: z.enum(["slide", "deck"]).nullable(),
  findings: z.number().int().nullable(),
  coverage: z.object({ slidesRead: z.number().int(), slidesTotal: z.number().int() }).nullable(),
  ok: z.boolean().nullable(),
});

/** How an issue came to be fixed: a re-check proved it (`verified`), the member said so
 * (`user_marked`), or SlideBrain wrote the fix without a re-check (`unverified`). Null for a
 * dismissal. */
export type ActivityProof = "verified" | "user_marked" | "unverified";

/** One action on an issue. */
export type ActivityAction = {
  id: string;
  at: string;
  userId: string;
  action: "fixed" | "dismissed";
  proof: ActivityProof | null;
  issueId: string | null;
  skillId: string | null;
  category: string | null;
  /** What SlideBrain said about the issue and the fix it proposed. Null when the firm
   * doesn't share finding text, or none was stored. */
  findingText: string | null;
};

const activityActionSchema = z.object({
  id: z.string(),
  at: z.string(),
  userId: z.string(),
  action: z.enum(["fixed", "dismissed"]),
  proof: z.enum(["verified", "user_marked", "unverified"]).nullable(),
  issueId: z.string().nullable(),
  skillId: z.string().nullable(),
  category: z.string().nullable(),
  findingText: z.string().nullable(),
});

/** `GET /v1/firm/activity` (`view_activity`). One of `runs` / `actions` is filled, per `kind`. */
export type ActivityResponse = {
  kind: ActivityKind;
  page: number;
  pageSize: number;
  hasMore: boolean;
  runs: ActivityRun[];
  actions: ActivityAction[];
  /** Everyone in the firm, for the member filter and the rows' names. */
  members: ActivityMember[];
  /** Skill ids the firm's activity names, for the skill filter. */
  skills: string[];
  /** The firm's finding-text setting (`orgs.share_finding_text`). */
  shareFindingText: boolean;
};

export const activityResponseSchema = z.object({
  kind: z.enum(["runs", "actions"]),
  page: z.number().int(),
  pageSize: z.number().int(),
  hasMore: z.boolean(),
  runs: z.array(activityRunSchema),
  actions: z.array(activityActionSchema),
  members: z.array(activityMemberSchema),
  skills: z.array(z.string()),
  shareFindingText: z.boolean(),
});

/** `GET /v1/firm/activity/summary?from&to` (`view_activity`): Home's "This month" strip
 * (D73). `from` inclusive, `to` exclusive (the panel sends the start of the viewer's month
 * and nothing after). */
export type ActivitySummaryQuery = { from: string; to?: string };

export const activitySummaryQuerySchema = z.object({ from: isoDate, to: isoDate.optional() });

/** One of the firm's most active members: who, and how many runs they finished. */
export type ActivityTopMember = { userId: string; email: string | null; name: string | null; runs: number };

export type ActivitySummary = {
  /** Skill runs finished. */
  runs: number;
  /** Issues fixed (verified, unverified or marked fixed by the member); dismissals don't count. */
  issuesFixed: number;
  /** Up to three members by runs finished, most first; members with no runs are left out. */
  topMembers: ActivityTopMember[];
};

export const activitySummarySchema = z.object({
  runs: z.number().int(),
  issuesFixed: z.number().int(),
  topMembers: z.array(activityMemberSchema.extend({ runs: z.number().int() })),
});

/** `PUT /v1/firm/settings` (`manage_members`) body and answer: the firm's settings. */
type FirmSettings = { shareFindingText: boolean };

export const firmSettingsSchema = z.object({ shareFindingText: z.boolean() });

expectParity<z.infer<typeof activityQuerySchema>, ActivityQuery>(true);
expectParity<z.infer<typeof activityResponseSchema>, ActivityResponse>(true);
expectParity<z.infer<typeof firmSettingsSchema>, FirmSettings>(true);
expectParity<z.infer<typeof activitySummaryQuerySchema>, ActivitySummaryQuery>(true);
expectParity<z.infer<typeof activitySummarySchema>, ActivitySummary>(true);
